import React from 'react';
import './MobileOverview.css';
import { getExtendedStats, formatPnL } from '../utils/stats';
import { getAggregatedStats } from '../utils/aggregatedStats';

function MobileOverview({ positions, selectedExchanges }) {
  if (!positions) {
    return (
      <div className="mobile-overview">
        <h1 className="mobile-header">Yield Dashboard</h1>
        <div className="loading-state">Loading...</div>
      </div>
    );
  }

  // Get aggregated stats across all exchanges
  const aggregated = getAggregatedStats(positions);

  // Get stats per exchange
  const exchangeStats = {};
  const exchanges = [
    { key: 'binance', name: 'Binance' },
    { key: 'bybit', name: 'Bybit' },
    { key: 'okx', name: 'OKX' },
    { key: 'hyperliquid', name: 'Hyperliquid' },
    { key: 'lighter', name: 'Lighter' },
    { key: 'extended', name: 'Extended' }
  ];

  exchanges.forEach(ex => {
    if (selectedExchanges[ex.key] && positions[ex.key]) {
      const exchangePositions = positions[ex.key].positions || [];
      exchangeStats[ex.key] = {
        ...ex,
        stats: getExtendedStats(exchangePositions),
        exchangeData: positions[ex.key]
      };
    }
  });

  // Calculate margin progress percentage
  const getMarginProgress = (exchangeData, stats) => {
    if (!exchangeData || !stats) return 0;
    const balance = exchangeData.total_balance || 0;
    if (balance === 0) return 0;
    // Use total initial margin if available, otherwise use total margin used
    const marginUsed = exchangeData.total_initial_margin || stats.totalMargin || 0;
    return Math.min(100, (marginUsed / balance) * 100);
  };

  return (
    <div className="mobile-overview">
      <h1 className="mobile-header">Yield Dashboard</h1>

      {/* Top Summary */}
      <div className="summary-section">
        <div className={`summary-item ${aggregated.totalAggFunding >= 0 ? 'positive' : 'negative'}`}>
          <div className="summary-label">Total Agg. Funding</div>
          <div className={`summary-value ${aggregated.totalAggFunding >= 0 ? 'positive' : 'negative'}`}>
            {formatPnL(aggregated.totalAggFunding)}
          </div>
        </div>
        <div className={`summary-item ${aggregated.avgYearlyYield >= 0 ? 'positive' : 'negative'}`}>
          <div className="summary-label">Avg. Yearly Yield</div>
          <div className={`summary-value ${aggregated.avgYearlyYield >= 0 ? 'positive' : 'negative'}`}>
            {aggregated.avgYearlyYield >= 0 ? '+' : ''}{aggregated.avgYearlyYield.toFixed(2)}%
          </div>
        </div>
      </div>

      {/* Exchange Cards */}
      <div className="exchange-cards">
        {Object.values(exchangeStats).map(({ key, name, stats, exchangeData }) => {
          if (!stats || !exchangeData) return null;

          const marginProgress = getMarginProgress(exchangeData, stats);
          const progressColor = marginProgress > 80 ? '#ef4444' : marginProgress > 60 ? '#f59e0b' : '#10b981';

          return (
            <div key={key} className="exchange-card">
              <div className="exchange-header">
                <h3 className="exchange-name">{name}</h3>
              </div>

              <div className="balance-section">
                <div className="balance-label">Balance</div>
                <div className="balance-value">${exchangeData.total_balance?.toFixed(2) || '0.00'}</div>
                <div className="margin-progress-bar">
                  <div 
                    className="margin-progress-fill" 
                    style={{ 
                      width: `${marginProgress}%`,
                      backgroundColor: progressColor
                    }}
                  />
                </div>
                <div className="margin-label">Margin</div>
              </div>

              <div className="metrics-grid">
                <div className="metric-item">
                  <div className="metric-label">Unrealized PnL</div>
                  <div className={`metric-value ${stats.totalUnrealizedPnl >= 0 ? 'positive' : 'negative'}`}>
                    {formatPnL(stats.totalUnrealizedPnl)}
                  </div>
                </div>
                <div className="metric-item">
                  <div className="metric-label">Total PnL</div>
                  <div className={`metric-value ${stats.totalPnL >= 0 ? 'positive' : 'negative'}`}>
                    {formatPnL(stats.totalPnL)}
                  </div>
                </div>
                <div className="metric-item">
                  <div className="metric-label">Agg. Funding</div>
                  <div className={`metric-value ${stats.totalFunding >= 0 ? 'positive' : 'negative'}`}>
                    {formatPnL(stats.totalFunding, 3)}
                  </div>
                </div>
                <div className="metric-item">
                  <div className="metric-label">Yield</div>
                  <div className={`metric-value ${stats.fundingPercentYearly >= 0 ? 'positive' : 'negative'}`}>
                    {stats.fundingPercentYearly >= 0 ? '+' : ''}{stats.fundingPercentYearly.toFixed(2)}%
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default MobileOverview;

